import { Box, Card, Group, Stack } from '@mantine/core';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { memo, useState } from 'react';

import { ConfirmModal } from '~/domains/common/components/Modal/Confirm/ConfirmModal';
import useInviteCardStyles from '~/domains/common/components/AlertCard/alertCard.styles';
import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import { Routes } from '~/domains/common/constants/routes';
import { formatDollars } from '~/domains/common/utils/formatters';
import { formatExpiry } from '~/domains/common/utils/formatExpiry';
import { Avatar } from '~/domains/design-system/Avatar';
import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';

import { bpsToPercent } from '../../utils/groupShareMath';
import { formatHandle } from '../../utils/formatHandle';
import { useDeclineGroupInvite } from '../../hooks/useGroupInviteActions';
import type { usePendingReceivedGroupInvites } from '../../hooks/useReceivedGroupInvites';

export type PendingInvite = ReturnType<
  typeof usePendingReceivedGroupInvites
>['pendingInvites'][number];

export const GroupRequestCard = memo(({ invite }: { invite: PendingInvite }) => {
  const { t } = useTranslation('group');
  const { classes, theme } = useInviteCardStyles();
  const router = useRouter();
  const [declineOpen, setDeclineOpen] = useState(false);
  const decline = useDeclineGroupInvite({ onSuccess: () => setDeclineOpen(false) });

  const { organizer, contest } = invite;
  const inviteHref = Routes.groupInvite(invite.id);

  const handleDecline = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDeclineOpen(true);
  };

  const handleAccept = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    router.push(inviteHref);
  };

  return (
    <>
      <Card className={classes.container} p="md" radius="lg">
        <Stack spacing={theme.other.spacing._12}>
          <UnstyledNextLink href={inviteHref} className={classes.contentLink}>
            <Text className={classes.inviterName}>
              {t('homeCard.wantsToJoin', { handle: formatHandle(organizer.handle) })}
            </Text>

            <Group className={classes.contestSection} spacing="sm">
              <Avatar
                handle={contest === null ? '' : contest.name}
                src={
                  contest === null
                    ? undefined
                    : (contest.thumbnailUrl ?? contest.commissioner?.avatarUrl ?? undefined)
                }
                alt={contest === null ? undefined : contest.name}
                size={40}
              />
              <Box className={classes.contestInfo}>
                <Title variant="headline-medium" className={classes.contestName}>
                  {contest === null ? '' : contest.name}
                </Title>
                <Group className={classes.contestMeta}>
                  <Text variant="body-medium" className={classes.metaItem}>
                    <span className={classes.metaLabel}>{t('homeCard.shareLabel')}</span>{' '}
                    <span className={classes.metaValue}>{bpsToPercent(invite.shareBps)}%</span>
                  </Text>
                  <Text className={classes.metaDivider} variant="body-small">
                    •
                  </Text>
                  <Text variant="body-medium" className={classes.metaItem}>
                    <span className={classes.metaLabel}>{t('homeCard.amountLabel')}</span>{' '}
                    <span className={classes.metaValue}>
                      {formatDollars(invite.priceCents / 100)}
                    </span>
                  </Text>
                </Group>
              </Box>
            </Group>
          </UnstyledNextLink>

          <Group grow spacing={theme.other.spacing._8}>
            <Button variant="tertiary-fill" size="small" onClick={handleDecline}>
              <Text variant="label-small">{t('homeCard.decline')}</Text>
            </Button>
            <Button variant="primary-fill" size="small" onClick={handleAccept}>
              <Text variant="label-small">
                {t('homeCard.accept', { amount: formatDollars(invite.priceCents / 100) })}
              </Text>
            </Button>
          </Group>

          <Text variant="body-medium" fw={500}>
            <span className={classes.expiryPrefix}>{t('homeCard.locks')} </span>
            <span className={classes.expiryTime}>{formatExpiry(invite.locksAt)}</span>
          </Text>
        </Stack>
      </Card>

      <ConfirmModal
        isOpen={declineOpen}
        title={t('acceptPage.declineModal.title')}
        description={t('acceptPage.declineModal.description')}
        confirmText={t('acceptPage.declineModal.confirm')}
        cancelText={t('acceptPage.declineModal.cancel')}
        onConfirm={() => decline.mutate(invite.id)}
        onClose={() => setDeclineOpen(false)}
        isOnConfirmLoading={decline.isLoading}
      />
    </>
  );
});
GroupRequestCard.displayName = 'GroupRequestCard';
